<template>
  <div class="additional-information" :class="componentLocationClass">
    <div class="additional-information__content">
      <p v-if="title" class="additional-information__chapter-title">
        {{ title }}
      </p>
      <h3 class="additional-information__title">Additional Materials</h3>

      <div
        v-if="commentaries.context"
        class="additional-information__section additional-information__section--context"
      >
        <h4 class="additional-information__section-title">Clarifications</h4>

        <div :class="textClasses">
          <div v-html="commentaries.context"></div>
        </div>
      </div>

      <div
        v-if="commentaries.counterarguments"
        class="additional-information__section additional-information__section--counterarguments"
      >
        <h4 class="additional-information__section-title">Counterarguments</h4>

        <div :class="textClasses">
          <div v-html="commentaries.counterarguments"></div>
        </div>
      </div>

      <div
        v-if="commentaries.actionables"
        class="additional-information__section additional-information__section--actionables"
      >
        <h4 class="additional-information__section-title">Actionables</h4>

        <div :class="textClasses">
          <div v-html="commentaries.actionables"></div>
        </div>
      </div>

      <div
        v-if="commentaries.learn_more && commentaries.learn_more.length > 0"
        class="additional-information__section additional-information__section--learn-more"
      >
        <h4 class="additional-information__section-title">Learn more</h4>

        <div>
          <LearnMoreItem
            v-for="item in commentaries.learn_more"
            :key="item.url"
            :class="learnMoreItemClasses"
            :item="item"
            :isPublicPage="isPublicPage"
          />
        </div>
      </div>

      <div
        v-if="
          commentaries.related_summaries &&
          commentaries.related_summaries.length > 0
        "
        class="additional-information__section additional-information__section--related-summary"
      >
        <h4 class="additional-information__section-title">
          Related Shortform Content
        </h4>

        <div>
          <RelatedSummaryItem
            v-for="item in commentaries.related_summaries"
            :key="item.url"
            :class="relatedSummaryItemClasses"
            :item="item"
          />
        </div>
      </div>

      <PaywallCTA v-if="isPublicPage" class="additional-information__cta">
        <template #title
          >Get access to the context and additional materials</template
        >
        <template #description
          >So you can understand the full picture and form your own
          opinion.</template
        >
        <template #button>Get access for free</template>
      </PaywallCTA>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { Commentaries } from '@/models/interfaces'

import LearnMoreItem from '@/app/components/doc/reading/LearnMoreItem.vue'
import PaywallCTA from '@/components/public/PaywallCTA.vue'
import RelatedSummaryItem from '@/app/components/doc/reading/RelatedSummaryItem.vue'

const props = withDefaults(
  defineProps<{
    commentaries: Commentaries
    isPublicPage?: boolean
    title?: string
    order?: number
  }>(),
  {
    isPublicPage: false,
  },
)

const textClasses = computed(() => {
  return addFade('additional-information__section-text')
})

const learnMoreItemClasses = computed(() => {
  return addFade('additional-information__learn-more-item')
})

const relatedSummaryItemClasses = computed(() => {
  return addFade('additional-information__related-summary-item')
})

const componentLocationClass = computed(() => {
  return props.isPublicPage
    ? 'additional-information--public'
    : 'additional-information--app'
})

/**
 * Add "--fade" modifier for all chapters except the first one.
 *
 * @param baseClass: base class of the additional information part.
 */
function addFade(baseClass: string): string[] {
  const classes = [baseClass]
  if (props.order !== 0 && props.isPublicPage) {
    classes.push(`${baseClass}--fade`)
  }
  return classes
}
</script>

<style lang="scss" scoped>
.additional-information {
  border-radius: 12px;
  padding: 40px;
  background: $c-bg;

  &--app {
    @media only screen and (max-width: $w-tablet-s) {
      // The sum of the paddings of `.content-editor` and `.container`, which contain this
      // element. We use this value to make this element full width on tablet and mobile screens.
      --content-editor-mobile-padding: calc(
        var(--content-editor-padding) + var(--container-padding)
      );
      position: relative;
      padding-inline: var(--content-editor-mobile-padding);
      // Add the container's padding to the width, making it have the full width of the container.
      width: calc(100% + var(--content-editor-mobile-padding) * 2);
      // Multiplying by -1 to make the variable value negative
      left: calc(var(--content-editor-mobile-padding) * -1);
      // This rule negates the bottom padding of `.content-editor`
      // Ideally we would add `padding-bottom: 0` in `.content-editor:has(.additional-information)`,
      // but Firefox still doesn't support `:has()`
      margin-bottom: calc(var(--content-editor-padding) * -1);
    }
  }

  &__chapter-title {
    color: $c-black-3;
    font-size: 20px;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: -0.4px;
    margin-bottom: 4px;
  }

  &__title {
    color: $c-black-2;
    font-size: 28px;
    font-weight: 500;
    line-height: 40px;
    letter-spacing: -0.64px;
    padding-top: 0;
    padding-bottom: 28px;
    margin: 0;
    border-top: none;
    border-bottom: 1px solid $c-black-13;
  }

  &__section {
    &--learn-more,
    &--related-summary {
      margin-top: 32px;
      border-top: 1px solid $c-black-13;

      &:nth-child(2) {
        margin-top: 0;
        border-top: none;
      }
    }

    &-title {
      color: $c-black-2;
      font-size: 24px;
      font-weight: 500;
      line-height: 24px;
      margin-top: 32px;
      margin-bottom: 12px;
    }

    &-text {
      position: relative;
      margin-bottom: 64px;

      :deep(p),
      :deep(li),
      :deep(blockquote) {
        color: $c-black-3;
        font-size: 18px;
        line-height: 28px;
        letter-spacing: -0.36px;

        @media only screen and (max-width: $w-medium-s) {
          font-size: 18px;
          line-height: 32px;
          letter-spacing: -0.36px;
        }
      }

      :deep(li:before) {
        background-color: $c-black-3;
      }

      :deep(> div > ul) {
        padding: 0;
      }

      &--fade {
        &:after {
          content: '';
          display: block;
          width: 100%;
          height: 100px;
          background: linear-gradient(
            0deg,
            rgba($c-bg, 1) 0%,
            rgba($c-bg, 0.7) 65%,
            rgba($c-bg, 0) 100%
          );
          position: absolute;
          bottom: 0;
        }
      }
    }
  }
  &__cta {
    margin-top: 28px;
  }

  &__learn-more-item {
    &--fade {
      &:last-of-type {
        position: relative;
        &::after {
          content: '';
          display: block;
          width: 100%;
          height: 70px;
          background: linear-gradient(
            0deg,
            rgba($c-bg, 1) 0%,
            rgba($c-bg, 0.7) 65%,
            rgba($c-bg, 0) 100%
          );
          position: absolute;
          bottom: -1px;
          left: 0;
        }
      }
    }
  }

  &--public {
    border-radius: 20px;
    width: 1260px;
    padding: 100px 0 40px 80px;
    margin-top: -40px;
    margin-left: -60px;

    @media only screen and (max-width: $w-large-s) {
      width: 1020px;
    }
    @media only screen and (max-width: $w-tablet-s) {
      margin-left: -48px;
      width: calc(100% + 96px);
      padding-left: 40px;
      padding-right: 40px;
    }
    @media only screen and (max-width: $w-medium-s) {
      margin-left: -32px;
      width: calc(100% + 64px);
      padding-right: 28px;
    }
    .additional-information {
      &__content {
        width: 640px;
        @media only screen and (max-width: $w-large-s) {
          width: 540px;
        }
        @media only screen and (max-width: $w-tablet-s) {
          width: 100%;
        }
      }

      &__section {
        &--learn-more,
        &--related-summary {
          &:nth-child(3) {
            margin-top: 0;
            border-top: none;
          }
        }
      }

      &__title {
        font-size: 32px;
        padding-bottom: 24px;
      }
    }
  }
}

.night-mode .additional-information {
  background-color: rgba($color: $c-black-9, $alpha: 0.7);

  &__title,
  &__section-title {
    color: $c-white;
  }

  &__section-text {
    :deep(p),
    :deep(li),
    :deep(blockquote) {
      color: $c-black-13;
    }

    :deep(li:before) {
      background-color: $c-black-13;
    }
  }

  &__title,
  &__section {
    border-color: $c-black-10;
  }
}
</style>
